import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import {
  doc,
  setDoc,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';

/* ---------- Context ---------- */
export const AuthContext = createContext({
  user: null,
  setUser: () => {},
  signup: async () => {},
  login: async () => {},
  loginWithGoogle: async () => {},
  logout: () => {}
});

/* ---------- Provider ---------- */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  /* 监听 Firebase 登录状态 */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async fbUser => {
      if (fbUser) {
        const snap = await getDoc(doc(db, 'users', fbUser.uid));
        const data = snap.exists() ? snap.data() : {};

        setUser({
          uid: fbUser.uid,
          email: fbUser.email,
          iconName: data.iconName || '',
          builderConfig: data.builderConfig || { base: 'Body_010', accessories: [] },
          role: data.role || 'viewer'            // 默认 viewer
        });
      } else {
        setUser(null);
      }
    });
    return unsubscribe;
  }, []);

  /* ---------- Email / Password ---------- */
  const signup = async ({ email, password }) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await setDoc(doc(db, 'users', cred.user.uid), {
      email,
      createdAt: serverTimestamp(),
      builderConfig: { base: 'Body_010', accessories: [] },
      role: 'viewer'                            // 新用户默认 viewer
    });
  };

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  /* ---------- Google ---------- */
  const googleProvider = new GoogleAuthProvider();

  const loginWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    const fbUser = result.user;

    const userRef = doc(db, 'users', fbUser.uid);
    const snap = await getDoc(userRef);

    if (!snap.exists()) {
      // 首次登录写入默认数据
      await setDoc(userRef, {
        email: fbUser.email,
        createdAt: serverTimestamp(),
        builderConfig: { base: 'Body_010', accessories: [] },
        role: 'viewer'
      });
    }

    const data = snap.exists() ? snap.data() : {};

    setUser({
      uid: fbUser.uid,
      email: fbUser.email,
      iconName: data.iconName || '',
      builderConfig: data.builderConfig || { base: 'Body_010', accessories: [] },
      role: data.role || 'viewer'
    });
  };

  /* ---------- Logout ---------- */
  const logout = () => signOut(auth);

  /* ---------- Context Value ---------- */
  const value = { user, setUser, signup, login, loginWithGoogle, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ---------- 自定义 Hook ---------- */
export const useAuth = () => useContext(AuthContext);
